/*
 * Mock data
 * ---------
 * Temporary in-memory data used while the backend is being built. Swap each
 * `getX()` helper for a real API call later — the page components only depend
 * on the shape of the returned object.
 */

// Stage labels — used by the venture sidebar/dashboard badges.
export const STAGE_LABELS = {
  sprout: "Sprout",
  seedling: "Seedling",
  sapling: "Sapling",
  tree: "Tree",
  redwood: "Redwood",
};

// Numeric "graduation level" per stage, used by admin pages for sortable
// tables and revenue-by-level charts.
export const STAGE_LEVELS = {
  seedling: 1,
  sprout: 2,
  sapling: 3,
  tree: 4,
  redwood: 5,
};

// Risk level labels + class suffixes (the admin shared CSS defines
// `.admin-badge-risk-{low,medium,high}` styles).
export const RISK_LABELS = {
  low: "Low",
  medium: "Medium",
  high: "High",
};

// Investment readiness labels for the dashboard's readiness indicator.
export const READINESS_LABELS = {
  not_ready: "Not Ready",
  early: "Early Stage",
  developing: "Developing",
  ready: "Investment Ready",
  ready_advanced: "Advanced",
};

// Sector labels — used by funder pages for charts and tables.
export const SECTOR_LABELS = {
  agriculture: "Agriculture",
  health: "Health",
  education: "Education",
  technology: "Technology",
  energy: "Energy",
  manufacturing: "Manufacturing",
  services: "Services",
};

// Gender / leadership classification labels.
export const GENDER_LABELS = {
  women_led: "Women-Led",
  women_and_youth_led: "Women & Youth-Led",
  youth_led: "Youth-Led",
};

// Format a number as Ugandan shillings (e.g. "UGX 12,500,000").
export function formatUGX(value) {
  if (value == null || Number.isNaN(value)) return "UGX 0";
  return "UGX " + Number(value).toLocaleString("en-UG");
}

/* ====================================================================== *
 * Portfolio fixtures
 * ----------------------------------------------------------------------
 * A small but representative slice of ventures across sectors, regions and
 * cohorts so the funder dashboards have something interesting to render.
 * ID "1" stays mapped to the seeded `venture@business.com` user.
 * ====================================================================== */

export const mockVentures = [
  {
    id: "1",
    name: "Green Harvest Foods",
    sector: "agriculture",
    geography: "Kampala",
    cohort: "2024-Q1",
    stage: "tree",
    status: "active",
    genderClassification: "women_led",
    founderName: "Grace Nakimuli",
    primaryProduct: "Organic vegetables",
    beneficiariesReached: 2450,
    milestones: ["first_revenue", "breakeven", "external_funding"],
  },
  {
    id: "2",
    name: "TechSister Solutions",
    sector: "technology",
    geography: "Kampala",
    cohort: "2023-Q3",
    stage: "redwood",
    status: "graduated",
    genderClassification: "women_led",
    founderName: "Fatima Nabirye",
    primaryProduct: "Mobile payments platform",
    beneficiariesReached: 12000,
    milestones: ["first_revenue", "breakeven", "external_funding", "scale"],
  },
  {
    id: "3",
    name: "Afya Clinic Network",
    sector: "health",
    geography: "Gulu",
    cohort: "2023-Q4",
    stage: "sapling",
    status: "active",
    genderClassification: "women_led",
    founderName: "Dr. Mary Namutebi",
    primaryProduct: "Mobile clinic services",
    beneficiariesReached: 8500,
    milestones: ["first_revenue", "breakeven"],
  },
  {
    id: "4",
    name: "Clean Water Initiative",
    sector: "energy",
    geography: "Lira",
    cohort: "2024-Q2",
    stage: "seedling",
    status: "active",
    genderClassification: "women_and_youth_led",
    founderName: "Sarah Namukasa",
    primaryProduct: "Solar water purification",
    beneficiariesReached: 5200,
    milestones: ["first_revenue"],
  },
  {
    id: "5",
    name: "BrightStep Academy",
    sector: "education",
    geography: "Mbarara",
    cohort: "2024-Q1",
    stage: "seedling",
    status: "active",
    genderClassification: "youth_led",
    founderName: "Patience Akello",
    primaryProduct: "After-school tutoring",
    beneficiariesReached: 1320,
    milestones: ["first_revenue"],
  },
  {
    id: "6",
    name: "Kasubi Crafts",
    sector: "manufacturing",
    geography: "Jinja",
    cohort: "2023-Q3",
    stage: "tree",
    status: "active",
    genderClassification: "women_led",
    founderName: "Joan Kirabo",
    primaryProduct: "Handcrafted home goods",
    beneficiariesReached: 980,
    milestones: ["first_revenue", "breakeven"],
  },
  {
    id: "7",
    name: "Pearl Logistics",
    sector: "services",
    geography: "Mbale",
    cohort: "2024-Q2",
    stage: "sprout",
    status: "active",
    genderClassification: "women_and_youth_led",
    founderName: "Esther Achieng",
    primaryProduct: "Last-mile delivery",
    beneficiariesReached: 540,
    milestones: [],
  },
  {
    id: "8",
    name: "AgriTech Cooperative",
    sector: "agriculture",
    geography: "Mbarara",
    cohort: "2023-Q4",
    stage: "sapling",
    status: "active",
    genderClassification: "youth_led",
    founderName: "Brenda Asiimwe",
    primaryProduct: "Cold-chain produce",
    beneficiariesReached: 3100,
    milestones: ["first_revenue", "breakeven"],
  },
];

export const mockVentureMetrics = [
  {
    ventureId: "1",
    revenue: 12500000,
    revenueGrowth3m: 18,
    jobsFullTime: 6,
    jobsPartTime: 4,
    capitalReceived: 45000000,
    externalCapital: 30000000,
    repaymentStatus: "on_track",
  },
  {
    ventureId: "2",
    revenue: 38000000,
    revenueGrowth3m: 22,
    jobsFullTime: 18,
    jobsPartTime: 7,
    capitalReceived: 90000000,
    externalCapital: 220000000,
    repaymentStatus: "completed",
  },
  {
    ventureId: "3",
    revenue: 9200000,
    revenueGrowth3m: 9,
    jobsFullTime: 5,
    jobsPartTime: 6,
    capitalReceived: 60000000,
    externalCapital: 25000000,
    repaymentStatus: "on_track",
  },
  {
    ventureId: "4",
    revenue: 4100000,
    revenueGrowth3m: 14,
    jobsFullTime: 3,
    jobsPartTime: 5,
    capitalReceived: 30000000,
    externalCapital: 18000000,
    repaymentStatus: "behind",
  },
  {
    ventureId: "5",
    revenue: 2900000,
    revenueGrowth3m: 6,
    jobsFullTime: 2,
    jobsPartTime: 4,
    capitalReceived: 15000000,
    externalCapital: 5000000,
    repaymentStatus: "on_track",
  },
  {
    ventureId: "6",
    revenue: 6700000,
    revenueGrowth3m: 11,
    jobsFullTime: 4,
    jobsPartTime: 8,
    capitalReceived: 40000000,
    externalCapital: 22000000,
    repaymentStatus: "on_track",
  },
  {
    ventureId: "7",
    revenue: 1800000,
    revenueGrowth3m: 4,
    jobsFullTime: 1,
    jobsPartTime: 3,
    capitalReceived: 10000000,
    externalCapital: 0,
    repaymentStatus: "not_applicable",
  },
  {
    ventureId: "8",
    revenue: 7400000,
    revenueGrowth3m: 12,
    jobsFullTime: 4,
    jobsPartTime: 5,
    capitalReceived: 35000000,
    externalCapital: 14000000,
    repaymentStatus: "on_track",
  },
];

// Outcome surveys (one baseline + one or two follow-ups per venture).
export const mockOutcomeSurveys = [
  {
    id: "s1-b",
    ventureId: "1",
    surveyType: "baseline",
    date: "2024-02-01",
    qualityOfLifeScore: 2.6,
  },
  {
    id: "s1-m",
    ventureId: "1",
    surveyType: "midline",
    date: "2025-09-30",
    qualityOfLifeScore: 4.1,
  },
  {
    id: "s1-e",
    ventureId: "1",
    surveyType: "endline",
    date: "2026-01-31",
    qualityOfLifeScore: 4.3,
  },
  {
    id: "s2-b",
    ventureId: "2",
    surveyType: "baseline",
    date: "2023-08-01",
    qualityOfLifeScore: 3.0,
  },
  {
    id: "s2-e",
    ventureId: "2",
    surveyType: "endline",
    date: "2025-12-15",
    qualityOfLifeScore: 4.5,
  },
  {
    id: "s3-b",
    ventureId: "3",
    surveyType: "baseline",
    date: "2023-11-01",
    qualityOfLifeScore: 2.8,
  },
  {
    id: "s3-m",
    ventureId: "3",
    surveyType: "midline",
    date: "2025-05-20",
    qualityOfLifeScore: 3.9,
  },
  {
    id: "s4-b",
    ventureId: "4",
    surveyType: "baseline",
    date: "2024-05-01",
    qualityOfLifeScore: 2.4,
  },
  {
    id: "s4-m",
    ventureId: "4",
    surveyType: "midline",
    date: "2025-11-10",
    qualityOfLifeScore: 3.6,
  },
  {
    id: "s5-b",
    ventureId: "5",
    surveyType: "baseline",
    date: "2024-03-01",
    qualityOfLifeScore: 2.9,
  },
  {
    id: "s5-m",
    ventureId: "5",
    surveyType: "midline",
    date: "2025-10-05",
    qualityOfLifeScore: 3.7,
  },
  {
    id: "s6-b",
    ventureId: "6",
    surveyType: "baseline",
    date: "2023-08-15",
    qualityOfLifeScore: 2.7,
  },
  {
    id: "s6-m",
    ventureId: "6",
    surveyType: "midline",
    date: "2025-04-20",
    qualityOfLifeScore: 4.0,
  },
  {
    id: "s8-b",
    ventureId: "8",
    surveyType: "baseline",
    date: "2023-11-15",
    qualityOfLifeScore: 2.5,
  },
  {
    id: "s8-m",
    ventureId: "8",
    surveyType: "midline",
    date: "2025-06-10",
    qualityOfLifeScore: 3.8,
  },
];

// Aggregate portfolio stats (precomputed for headline cards).
export const portfolioStats = {
  totalVentures: mockVentures.length,
  womenLedVentures: mockVentures.filter(
    (venture) => venture.genderClassification === "women_led",
  ).length,
  totalRevenue: mockVentureMetrics.reduce(
    (sum, metric) => sum + metric.revenue,
    0,
  ),
  totalJobsCreated: mockVentureMetrics.reduce(
    (sum, metric) =>
      sum + (metric.jobsFullTime || 0) + (metric.jobsPartTime || 0),
    0,
  ),
  totalCapitalDeployed: mockVentureMetrics.reduce(
    (sum, metric) => sum + (metric.capitalReceived || 0),
    0,
  ),
  totalExternalCapital: mockVentureMetrics.reduce(
    (sum, metric) => sum + (metric.externalCapital || 0),
    0,
  ),
  survivalRate6Months: 94,
  survivalRate12Months: 86,
  averageQoLImprovement: 38,
};

/* ====================================================================== *
 * Admin-facing extensions
 * ----------------------------------------------------------------------
 * The admin/manager pages need a few extra fields per venture (founder
 * email, team size, last-update timestamp, completeness score), a
 * `revenueGrowth6m` companion on each metric row, and a small due-diligence
 * fixture used by the portfolio performance table.
 *
 * We patch the existing arrays in-place so funder/venture pages keep
 * working unchanged.
 * ====================================================================== */

const ADMIN_VENTURE_PATCH = {
  1: {
    founderEmail: "grace@greenharvest.ug",
    teamSize: 10,
    lastDataUpdate: "2026-01-12",
    dataCompleteness: 92,
  },
  2: {
    founderEmail: "fatima@techsister.io",
    teamSize: 25,
    lastDataUpdate: "2026-01-18",
    dataCompleteness: 96,
  },
  3: {
    founderEmail: "mary@afyaclinic.ug",
    teamSize: 11,
    lastDataUpdate: "2026-01-09",
    dataCompleteness: 81,
  },
  4: {
    founderEmail: "sarah@cleanwater.ug",
    teamSize: 8,
    lastDataUpdate: "2025-11-02",
    dataCompleteness: 58,
  },
  5: {
    founderEmail: "patience@brightstep.ug",
    teamSize: 6,
    lastDataUpdate: "2026-01-15",
    dataCompleteness: 74,
  },
  6: {
    founderEmail: "joan@kasubicrafts.ug",
    teamSize: 12,
    lastDataUpdate: "2026-01-04",
    dataCompleteness: 88,
  },
  7: {
    founderEmail: "esther@pearllogistics.ug",
    teamSize: 4,
    lastDataUpdate: null,
    dataCompleteness: 35,
  },
  8: {
    founderEmail: "brenda@agritech.coop",
    teamSize: 9,
    lastDataUpdate: "2026-01-10",
    dataCompleteness: 79,
  },
};

mockVentures.forEach((venture) =>
  Object.assign(venture, ADMIN_VENTURE_PATCH[venture.id] || {}),
);

// 6-month growth = ~3-month growth × small variance (so the chart shows two
// bars per venture and isn't a flat copy).
const SIX_MONTH_FACTORS = {
  1: 1.4,
  2: 1.6,
  3: 1.2,
  4: 0.9,
  5: 1.1,
  6: 1.3,
  7: 0.6,
  8: 1.5,
};

mockVentureMetrics.forEach((metric) => {
  metric.revenueGrowth6m = Math.round(
    (metric.revenueGrowth3m || 0) *
      (SIX_MONTH_FACTORS[metric.ventureId] ?? 1.3),
  );
});

// Due-diligence snapshot used by Portfolio Performance.
export const mockDueDiligence = [
  { ventureId: "1", performanceScore: 84, riskLevel: "low" },
  { ventureId: "2", performanceScore: 92, riskLevel: "low" },
  { ventureId: "3", performanceScore: 71, riskLevel: "medium" },
  { ventureId: "4", performanceScore: 48, riskLevel: "high" },
  { ventureId: "5", performanceScore: 62, riskLevel: "medium" },
  { ventureId: "6", performanceScore: 76, riskLevel: "low" },
  { ventureId: "7", performanceScore: 41, riskLevel: "high" },
  { ventureId: "8", performanceScore: 73, riskLevel: "medium" },
];

/* ====================================================================== *
 * Computation helpers — keep page components dumb.
 * ====================================================================== */

// % of repayable ventures that are on-track or completed.
export function calculateRepaymentRate(metrics = mockVentureMetrics) {
  const repayable = metrics.filter(
    (metric) => metric.repaymentStatus !== "not_applicable",
  );
  if (repayable.length === 0) return 0;
  const healthy = repayable.filter(
    (metric) =>
      metric.repaymentStatus === "on_track" ||
      metric.repaymentStatus === "completed",
  ).length;
  return Math.round((healthy / repayable.length) * 100);
}

// External capital ÷ Imagine Her capital, rounded to 1 decimal place.
export function calculateLeverageRatio(ihCapital, externalCapital) {
  if (!ihCapital) return 0;
  return Math.round((externalCapital / ihCapital) * 10) / 10;
}

/* ====================================================================== *
 * Single-venture lookups (used by the venture workspace).
 * ====================================================================== */

// Single demo venture record — kept for the seeded venture user.
const VENTURES = Object.fromEntries(
  mockVentures.map((venture) => [venture.id, venture]),
);
const METRICS = Object.fromEntries(
  mockVentureMetrics.map((metric) => [metric.ventureId, metric]),
);

// Latest due-diligence snapshot.
const DUE_DILIGENCE = {
  1: { performanceScore: 78, investmentReadiness: "developing" },
};

// History of outcome surveys grouped by venture (chronological).
const SURVEYS = mockOutcomeSurveys.reduce((acc, s) => {
  (acc[s.ventureId] ||= []).push(s);
  return acc;
}, {});

export function getVentureById(id) {
  return VENTURES[id] ?? null;
}

export function getMetricsByVentureId(id) {
  return METRICS[id] ?? null;
}

export function getDueDiligenceByVentureId(id) {
  return DUE_DILIGENCE[id] ?? null;
}

export function getSurveysByVentureId(id) {
  return SURVEYS[id] ?? [];
}
